/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useMemo } from 'react';
import { LAYOUT2 } from '../Styles';

const CONTAINER_STYLE = css`
  label: CONTAINER_STYLE;
  display: grid;
  gap: ${LAYOUT2.spacing2}px;
  align-items: start;
`;

const COLUMN_STYLE = css`
  label: COLUMN_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
`;

export const ColumnsLayout = <T,>({
  items,
  columnCount = 1,
  fillDirection = 'column-first',
  renderItem,
  columnStyle,
  ...containerProps
}: {
  items: T[];
  columnCount?: number;
  fillDirection?: 'row-first' | 'column-first';
  renderItem?: (item: T, index: number) => React.ReactNode;
  columnStyle?: ReturnType<typeof css> | Array<ReturnType<typeof css> | undefined | false>;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>) => {
  const columns = useMemo(() => {
    if (items.length === 0) return [];

    const result: T[][] = Array.from({ length: columnCount }, () => []);

    if (fillDirection === 'column-first') {
      const itemsPerColumn = Math.ceil(items.length / columnCount);
      items.forEach((item, index) => {
        const columnIndex = Math.floor(index / itemsPerColumn);
        if (columnIndex < columnCount) {
          result[columnIndex].push(item);
        }
      });
    } else {
      items.forEach((item, index) => {
        const columnIndex = index % columnCount;
        result[columnIndex].push(item);
      });
    }

    return result;
  }, [items, columnCount, fillDirection]);

  if (items.length === 0) return null;

  return (
    <div
      css={[CONTAINER_STYLE, css`grid-template-columns: repeat(${columnCount}, 1fr);`]}
      {...containerProps}
    >
      {columns.map((columnItems, columnIndex) => (
        <div key={columnIndex} css={[COLUMN_STYLE, columnStyle]}>
          {columnItems.map((item, itemIndex) => {
            const globalIndex = fillDirection === 'column-first'
              ? columnIndex * Math.ceil(items.length / columnCount) + itemIndex
              : itemIndex * columnCount + columnIndex;
            return (
              <React.Fragment key={globalIndex}>
                {renderItem ? renderItem(item, globalIndex) : (item as React.ReactNode)}
              </React.Fragment>
            );
          })}
        </div>
      ))}
    </div>
  );
};
